import { z } from "zod";

import type { BulkImportCampaignInfluencerRow } from "@/marketing/models/campaignInfluencer";

import {
  STANDARD_PIPELINE_DEFINITION,
  type CampaignPipelineDefinition,
} from "@/marketing/models/campaignPipeline";

const EMAIL_SCHEMA = z.email();

export interface ParseCampaignCsvResult {
  rows: BulkImportCampaignInfluencerRow[];
  errors: string[];
}

const REQUIRED_HEADERS = ["handle", "platform", "stage"] as const;

const normalizeStageToken = (value: string): string =>
  value.replace(/\s+/g, "").toLowerCase();

// Accepts stage keys and admin labels (what the CSV export writes), case/space-insensitive; keys win over labels.
function buildStageLookup(
  definition: CampaignPipelineDefinition,
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const { stage, labels } of definition.stages) {
    lookup.set(normalizeStageToken(labels.admin), stage);
  }
  for (const { stage } of definition.stages) {
    lookup.set(normalizeStageToken(stage), stage);
  }
  return lookup;
}

export function parseCampaignCsv(
  content: string,
  definition: CampaignPipelineDefinition = STANDARD_PIPELINE_DEFINITION,
): ParseCampaignCsvResult {
  const stageLookup = buildStageLookup(definition);
  const rows: BulkImportCampaignInfluencerRow[] = [];
  const errors: string[] = [];

  const lines = content
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    errors.push("CSV is empty");
    return { rows, errors };
  }

  const headerCells = splitCsvLine(lines[0] ?? "").map((h) =>
    h.trim().toLowerCase(),
  );
  for (const required of REQUIRED_HEADERS) {
    if (!headerCells.includes(required)) {
      errors.push(`Missing required column: ${required}`);
    }
  }
  if (errors.length > 0) return { rows, errors };

  const headerIdx = (name: string): number => headerCells.indexOf(name);
  const handleIdx = headerIdx("handle");
  const platformIdx = headerIdx("platform");
  const stageIdx = headerIdx("stage");
  const settlementIdx = headerIdx("settlement_amount");
  const notesIdx = headerIdx("notes");
  const selectedSkusIdx = headerIdx("selected_skus");
  const emailIdx = headerIdx("email");

  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i] ?? "");
    const lineNo = i + 1;

    const rawHandle = (cells[handleIdx] ?? "").trim();
    const rawPlatform = (cells[platformIdx] ?? "").trim().toLowerCase();
    const rawStage = (cells[stageIdx] ?? "").trim();

    if (rawHandle.length === 0) {
      errors.push(`Line ${lineNo}: handle is empty`);
      continue;
    }
    if (rawPlatform.length === 0) {
      errors.push(`Line ${lineNo}: platform is empty`);
      continue;
    }
    const stage = stageLookup.get(normalizeStageToken(rawStage));
    if (!stage) {
      errors.push(
        `Line ${lineNo}: stage "${rawStage}" is not a valid pipeline stage`,
      );
      continue;
    }

    const row: BulkImportCampaignInfluencerRow = {
      handle: rawHandle.replace(/^@/, ""),
      platform: rawPlatform,
      stage,
    };

    if (settlementIdx >= 0) {
      const raw = (cells[settlementIdx] ?? "").trim();
      if (raw.length > 0) {
        const cleaned = raw.replace(/[$,]/g, "");
        const num = Number(cleaned);
        if (!Number.isFinite(num) || num < 0) {
          errors.push(
            `Line ${lineNo}: settlement_amount "${raw}" is not a non-negative number`,
          );
          continue;
        }
        row.settlementAmount = num;
      }
    }

    if (notesIdx >= 0) {
      const raw = (cells[notesIdx] ?? "").trim();
      if (raw.length > 0) {
        row.notes = raw;
      }
    }

    // Multiple SKUs are separated by ";".
    if (selectedSkusIdx >= 0) {
      const raw = (cells[selectedSkusIdx] ?? "").trim();
      if (raw.length > 0) {
        const skus = raw
          .split(";")
          .map((sku) => sku.trim())
          .filter((sku) => sku.length > 0);
        if (skus.length === 0) {
          errors.push(`Line ${lineNo}: selected_skus "${raw}" contains no usable SKU`);
          continue;
        }
        row.selectedSkus = skus;
      }
    }

    if (emailIdx >= 0) {
      const raw = (cells[emailIdx] ?? "").trim();
      if (raw.length > 0) {
        if (EMAIL_SCHEMA.safeParse(raw).success) {
          row.email = raw.toLowerCase();
        } else {
          errors.push(`Line ${lineNo}: email "${raw}" is not a valid email address`);
          continue;
        }
      }
    }

    rows.push(row);
  }

  return { rows, errors };
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === ",") {
        cells.push(current);
        current = "";
      } else if (ch === '"') {
        inQuotes = true;
      } else {
        current += ch;
      }
    }
  }
  cells.push(current);
  return cells;
}
